import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Copy, Check, RefreshCw, Linkedin, Twitter, Instagram, Facebook, Link2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, limit: 3000 },
  { key: "twitter", label: "X / Twitter", icon: Twitter, limit: 280 },
  { key: "instagram", label: "Instagram", icon: Instagram, limit: 2200 },
  { key: "facebook", label: "Facebook", icon: Facebook, limit: 63206 },
];
const PLATFORM_LABEL = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.label]));
const PLATFORM_LIMIT = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.limit]));
const PLATFORM_ICON = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.icon]));

const TONES = [
  { value: "professional", label: "Professional / thought-leadership" },
  { value: "casual", label: "Casual / conversational" },
  { value: "punchy", label: "Punchy / high-energy" },
  { value: "inspirational", label: "Inspirational" },
];

function composePost(text, hashtags, publicUrl) {
  let out = (text || "").trim();
  if (hashtags && hashtags.length) out += `\n\n${hashtags.join(" ")}`;
  if (publicUrl) out += `\n\n${publicUrl}`;
  return out;
}

function PlatformResultCard({ platformKey, entry, publicUrl, postId }) {
  const Icon = PLATFORM_ICON[platformKey] || Megaphone;
  const [text, setText] = useState(entry?.text || "");
  const [saved, setSaved] = useState(entry?.text || "");
  const [copied, setCopied] = useState(false);
  const hashtags = entry?.hashtags || [];

  useEffect(() => { setText(entry?.text || ""); setSaved(entry?.text || ""); }, [entry, postId]);

  const composed = composePost(text, hashtags, publicUrl);
  const limit = PLATFORM_LIMIT[platformKey] || 3000;
  const over = composed.length > limit;

  const persist = async () => {
    if (text === saved || !postId) return;
    try {
      await api.put(`/social/posts/${postId}`, { platform: platformKey, text });
      setSaved(text);
    } catch { /* non-blocking */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(composed);
      setCopied(true);
      toast.success(`${PLATFORM_LABEL[platformKey]} post copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  return (
    <Card className="border-border" data-testid={`social-result-${platformKey}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Icon className="h-4 w-4 text-primary" />{PLATFORM_LABEL[platformKey]}
          </div>
          <span className={`text-xs tabular-nums ${over ? "font-semibold text-red-600" : "text-muted-foreground"}`} data-testid={`social-charcount-${platformKey}`}>
            {composed.length}{limit < 1000 ? ` / ${limit}` : ""}
          </span>
        </div>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={persist}
          rows={platformKey === "twitter" ? 3 : 5}
          className="resize-none text-sm"
          data-testid={`social-text-${platformKey}`}
        />

        {hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {hashtags.map((h) => <Badge key={h} variant="secondary" className="font-normal">{h}</Badge>)}
          </div>
        )}

        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid={`social-link-${platformKey}`}>
            <Link2 className="h-3.5 w-3.5" />{publicUrl}
          </a>
        )}

        <div className="flex items-center justify-between">
          {over ? <span className="text-xs text-red-600">Over the {limit}-char limit</span> : <span />}
          <Button size="sm" variant={copied ? "secondary" : "default"} onClick={copy} className={copied ? "" : "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"} data-testid={`social-copy-${platformKey}`}>
            {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}{copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function SocialPostGenerator({ source, initialPost, onGenerated }) {
  const effectiveSource = source || (initialPost && {
    type: initialPost.source_type, id: initialPost.source_id,
    title: initialPost.source_title, public_url: initialPost.public_url,
  });
  const [tone, setTone] = useState(initialPost?.tone || "professional");
  const [platforms, setPlatforms] = useState(initialPost?.platforms || PLATFORMS.map((p) => p.key));
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState(initialPost || null);

  useEffect(() => {
    // Reset when the selected source changes (but keep an explicitly provided initialPost)
    if (!initialPost) setPost(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.type, source?.id]);

  const togglePlatform = (key) => {
    setPlatforms((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const generate = async () => {
    if (!effectiveSource?.id) { toast.error("Pick a clip, recording, or video first"); return; }
    if (platforms.length === 0) { toast.error("Choose at least one platform"); return; }
    setLoading(true);
    try {
      const r = await api.post("/social/generate", {
        source_type: effectiveSource.type,
        source_id: effectiveSource.id,
        platforms,
        tone,
        public_url: effectiveSource.public_url || null,
      });
      setPost(r.data);
      onGenerated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate posts");
    } finally {
      setLoading(false);
    }
  };

  const orderedResults = useMemo(() => {
    if (!post?.results) return [];
    return PLATFORMS.filter((p) => post.results[p.key]).map((p) => [p.key, post.results[p.key]]);
  }, [post]);

  return (
    <div className="space-y-4" data-testid="social-generator">
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-secondary/30 p-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="w-[230px]" data-testid="social-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platforms</Label>
            <div className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => {
                const active = platforms.includes(p.key);
                const Icon = p.icon;
                return (
                  <Button key={p.key} type="button" size="sm" variant={active ? "default" : "outline"}
                    onClick={() => togglePlatform(p.key)}
                    className={active ? "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" : ""}
                    data-testid={`social-platform-${p.key}`} aria-pressed={active}>
                    <Icon className="mr-1.5 h-4 w-4" />{p.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
        <Button onClick={generate} disabled={loading || !effectiveSource?.id} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="social-generate-button">
          {loading ? <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" /> : (post ? <RefreshCw className="mr-1.5 h-4 w-4" /> : <Sparkles className="mr-1.5 h-4 w-4" />)}
          {loading ? "Generating…" : (post ? "Regenerate" : "Generate posts")}
        </Button>
      </div>

      {loading && (
        <div className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="border-border"><CardContent className="space-y-3 p-4">
              <Skeleton className="h-4 w-28" /><Skeleton className="h-24 w-full" /><Skeleton className="h-8 w-20" />
            </CardContent></Card>
          ))}
        </div>
      )}

      {!loading && post && (
        <div className="grid gap-3 md:grid-cols-2">
          {orderedResults.map(([key, entry]) => (
            <PlatformResultCard key={key} platformKey={key} entry={entry} publicUrl={post.public_url} postId={post.post_id} />
          ))}
        </div>
      )}

      {!loading && !post && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="social-empty">
          <Megaphone className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium text-foreground">Ready to promote {effectiveSource?.title ? `"${effectiveSource.title}"` : "your content"}</p>
          <p className="text-sm text-muted-foreground">Choose a tone and platforms, then generate ready-to-paste posts.</p>
        </div>
      )}
    </div>
  );
}

export function SocialPostDialog({ open, onOpenChange, source, initialPost }) {
  const title = source?.title || initialPost?.source_title || "this content";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" />Create social posts</DialogTitle>
          <DialogDescription>AI-written, platform-tailored posts for "{title}". Edit, then copy to your feeds.</DialogDescription>
        </DialogHeader>
        {(source || initialPost) && <SocialPostGenerator source={source} initialPost={initialPost} />}
      </DialogContent>
    </Dialog>
  );
}
